import { useState } from 'react';
import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import {
  adminSelectCalculationsData,
  adminSelectContextData,
  adminSelectFieldsData,
  adminSelectFieldsDefinitions,
} from 'features/admin/protocol-editor/store/adminProtocolEditorSelectors';
import { selectUser } from 'features/auth/store/authSelectors';
import { logger } from 'features/logger/logger';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import {
  getActivityTaskDocumentPath,
  getActivityTaskHistoryEntriesPath,
} from 'features/questionnaires/services/documentService';
import {
  selectDocData,
  selectFieldsDefinitions,
} from 'features/questionnaires/store/questionnaireSelectors';
import { useAppSelector } from 'features/store/hooks';

type Props = {
  slice: 'questionnaire' | 'adminProtocolEditor';
};

export const DevQuestionnaireMenu = ({ slice }: Props) => {
  const fieldsData = useAppSelector(state =>
    slice === 'questionnaire'
      ? selectDocData(state).fieldsData
      : adminSelectFieldsData(state)
  );
  const contextData = useAppSelector(state =>
    slice === 'questionnaire'
      ? selectDocData(state).contextData
      : adminSelectContextData(state)
  );
  const calculationsData = useAppSelector(state =>
    slice === 'questionnaire'
      ? selectDocData(state).calculationsData
      : adminSelectCalculationsData(state)
  );
  const fieldsDefinitions = useAppSelector(state =>
    slice === 'questionnaire'
      ? selectFieldsDefinitions(state)
      : adminSelectFieldsDefinitions(state)
  );
  const formId = useAppSelector(state =>
    slice === 'questionnaire' ? selectDocData(state).id : undefined
  );
  const taskDocumentPath = useAppSelector(state =>
    slice === 'questionnaire'
      ? getActivityTaskDocumentPath(
          selectUser(state).uid,
          selectParticipationDocData(state).id,
          selectDocData(state).activityName
        )
      : undefined
  );
  const taskHistoryEntriesPath = useAppSelector(state =>
    slice === 'questionnaire'
      ? getActivityTaskHistoryEntriesPath(
          selectUser(state).uid,
          selectParticipationDocData(state).id,
          selectDocData(state).activityName
        )
      : undefined
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const copyToClipboardAndClose = async (value: object) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
      closeMenu();
    } catch (error) {
      logger.error(
        'Unable to copy JSON to clipboard',
        undefined,
        error as Error
      );
    }
  };

  const queryAndCopyFormDocumentToCliboard = async () => {
    if (taskDocumentPath) {
      try {
        const { snapshot } = await FirebaseFirestore.getDocument({
          reference: taskDocumentPath,
        });
        void copyToClipboardAndClose({ id: snapshot.id, ...snapshot.data });
      } catch (error) {
        logger.error(
          'Unable to obtain form document',
          undefined,
          error as Error
        );
      }
    }
  };

  const queryAndCopyFormHistoryToCliboard = async () => {
    if (taskHistoryEntriesPath) {
      try {
        const { snapshots } = await FirebaseFirestore.getCollection({
          reference: taskHistoryEntriesPath,
          queryConstraints: [
            {
              type: 'orderBy',
              fieldPath: 'timestamp',
              directionStr: 'desc',
            },
          ],
        });
        const entries = snapshots.map(snap => ({
          id: snap.id,
          ...snap.data,
        }));
        void copyToClipboardAndClose(entries);
      } catch (error) {
        logger.error(
          'Unable to obtain form history entries',
          undefined,
          error as Error
        );
      }
    }
  };

  return (
    <Box>
      <IconButton
        id="dev-survey-menu-button"
        aria-label="Developer menu for questionnaire"
        aria-controls={open ? 'dev-survey-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={openMenu}
      >
        <ContentCopyIcon />
      </IconButton>
      <Menu
        id="dev-survey-menu"
        aria-labelledby="dev-survey-menu-button"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
      >
        <MenuItem onClick={() => void copyToClipboardAndClose(fieldsData)}>
          Copy fieldsData to JSON (Redux)
        </MenuItem>
        <MenuItem
          disabled={!contextData}
          onClick={() =>
            contextData && void copyToClipboardAndClose(contextData)
          }
        >
          Copy contextData to JSON (Redux)
        </MenuItem>
        <MenuItem
          onClick={() =>
            calculationsData && void copyToClipboardAndClose(calculationsData)
          }
        >
          Copy calculationsData to JSON (Redux)
        </MenuItem>
        <MenuItem
          onClick={() => void copyToClipboardAndClose(fieldsDefinitions)}
        >
          Copy fieldsDefinitions to JSON (Redux)
        </MenuItem>
        {/* Separate conditions are needed because MUI menu doesn't accept fragments */}
        {formId && (
          <MenuItem onClick={queryAndCopyFormDocumentToCliboard}>
            Query and copy form to JSON (Firestore)
          </MenuItem>
        )}
        {formId && (
          <MenuItem onClick={queryAndCopyFormHistoryToCliboard}>
            Query and copy form history entries to JSON (Firestore)
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
